const ApiResponseUnmapper = require('@/libraries/DigiTickets/Api/ApiResponseUnmapper');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const Order = require('@/lib/Orders/Order');
const { apiResponseToError } = require('@/lib/Api');

/**
 * Populates the orders table with orders for sessions soon in the future.
 */
class OrderDownloader {
    /**
     * @param {ApiRequestParams} apiRequestParams
     * @param {Hydrator} hydrator
     * @param {ItemInstanceOrderMapRepository} itemInstanceOrderMapRepository
     * @param {OrderRepository} orderRepository
     * @param orderResource
     */
    constructor(
        apiRequestParams,
        hydrator,
        itemInstanceOrderMapRepository,
        orderRepository,
        orderResource
    ) {
        this.apiRequestParams = apiRequestParams;
        this.hydrator = hydrator;
        this.itemInstanceOrderMapRepository = itemInstanceOrderMapRepository;
        this.orderRepository = orderRepository;
        this.orderResource = orderResource;

        /**
         * @type {ConsoleLogger}
         */
        this.log = logFactory('OrderDownloader');

        /**
         * @type {boolean}
         */
        this.downloadInProgress = false;

        /**
         * How many orders to download in each "chunk".
         *
         * @type {number}
         */
        this.pageSize = 100;
    }

    /**
     * @param {Function} [progressCallback] A function to be called after each page is loaded with the
     *                                      number of orders downloaded so far.
     *
     * @returns {Promise<unknown>}
     */
    download(progressCallback) {
        this.log.log('Downloading orders.');

        return new Promise((resolve, reject) => {
            this.downloadInProgress = true;

            // Empty existing items in the table.
            this.orderRepository.clear();
            this.itemInstanceOrderMapRepository.clear();

            let pageNumber = 1;
            let downloadedOrderCount = 0;

            const onFailure = (err) => {
                this.downloadInProgress = false;
                reject(err);
            };

            /**
             * @param {Array|null} ordersData
             */
            const onPageDownloaded = (ordersData) => {
                if (!(ordersData instanceof Array)) {
                    // The last downloaded page did not return any data, so we can assume that was the last
                    // of the orders and finish.
                    this.downloadInProgress = false;
                    resolve(downloadedOrderCount);

                    return;
                }

                this.downloadInProgress = true;
                downloadedOrderCount += ordersData.length;

                if (typeof progressCallback === 'function') {
                    progressCallback(downloadedOrderCount);
                }

                this.storeOrders(ordersData)
                    .then(() => {
                        // A crude method of determining if there is another page to download.
                        // If exactly "page size" number of orders were downloaded assume it was cut off
                        // and there is a next page. There is of course the chance that there were exactly
                        // this number of orders only, but this works the majority of the time, and it doesn't
                        // matter if it causes an extra request in rare situations.
                        if (ordersData.length === this.pageSize) {
                            ++pageNumber;
                            this.downloadPage(pageNumber)
                                .then(onPageDownloaded)
                                .catch(onFailure);

                            return;
                        }

                        this.downloadInProgress = false;
                        resolve(downloadedOrderCount);
                    });
            };

            this.downloadPage(pageNumber)
                .then(onPageDownloaded)
                .catch(onFailure);
        });
    }

    /**
     * @private
     * @param {number} pageNumber
     *
     * @returns {Promise<Array|null>}
     */
    downloadPage(pageNumber) {
        this.log.log(`Downloading page ${pageNumber}.`);

        return new Promise((resolve, reject) => {
            // Fetch every order made in the last year (up to 100) starting from the given page number.
            this.orderResource.get(
                {
                    // Use mappers to reduce the amount of duplicated data returned in the response.
                    // This previously used these:
                    // 'mappers[0][keyedBy]': 'bookingRef',
                    // 'mappers[0][normalise]': true,
                    // 'mappers[1][toTable]': true,
                    // 'mappers[2][encodeEachResult]': 'toJSON',
                    ...this.apiRequestParams.defaultMappers(),
                    ...{
                        ticketsValidFor: 'today',
                        dateStart: '1 year ago',
                        limit: this.pageSize,
                        page: pageNumber,
                        orderBy: 'orderID',
                        orderDirection: 'DESC',
                    },
                },
                (response) => {
                    // If data or columns is not set, there is no data on the page requested.
                    if (!response.hasOwnProperty('data') || !response.hasOwnProperty('columns')) {
                        resolve(null);

                        return;
                    }

                    // Undo the mappers
                    const unmappedResponse = ApiResponseUnmapper.unmap(response);

                    const count = unmappedResponse.length;
                    this.log.log(`Downloaded ${count} order(s).`);

                    resolve(unmappedResponse);
                },
                (err) => reject(apiResponseToError(err))
            );
        });
    }

    /**
     * @private
     * @param {object[]} orderData Array of order data rows from API.
     */
    storeOrders(orderData) {
        // Hydrate the data into Order models to we store the correct data.
        /** @type {Order[]} orders */
        let orders = this.hydrator.hydrateArray(
            orderData,
            () => new Order()
        );

        orders.forEach((order) => {
            let itemInstanceOrderMaps = [];
            order.items.forEach((orderLine) => {
                orderLine.iteminstances.forEach((itemInstance) => {
                    itemInstanceOrderMaps.push({
                        itemInstanceRef: itemInstance.itemInstanceRef,
                        itemInstanceID: itemInstance.ID,
                        orderID: order.ID,
                    });
                });
            });
            this.itemInstanceOrderMapRepository.persistMany(itemInstanceOrderMaps);
        });

        return this.orderRepository.persistMany(orders);
    }
}

module.exports = OrderDownloader;
